import { AlertTriangle, ChevronDown, ImageUp, X } from 'lucide-react'
import { useId, useState } from 'react'

import { useChannels } from '@/api/endpoints/channels'
import {
  useRunStudioThumbnails,
  useStudioThumbnails,
  useUnqueueStudioThumbnail,
  type StudioThumbnailQueueItemOut,
} from '@/api/endpoints/scheduler'
import { Button } from '@/components/ui/Button'
import { Chip } from '@/components/ui/Chip'
import { IconButton } from '@/components/ui/IconButton'
import { channelColor } from '@/lib/channelColor'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'
import { formatRelativeDate } from '@/lib/format'
import { attemptLabel, canRun, coversRunMessage, isPaused, statusLine, summarizeQueue } from './status'
import styles from './StudioThumbnailsBanner.module.scss'

/**
 * Faixa "Capas dos Shorts" (card #247): a fila de capas do usuário e o botão
 * que drena agora, o mesmo que o fim do ciclo de upload faz. Recolhida, diz
 * quantas esperam e por que falharam; aberta, lista a fila. Some quando não há
 * nada na fila. Só o admin dispara: a drenagem usa a conta Google do Prism e o
 * celular. Tirar um vídeo da fila é de qualquer dono, só nos próprios canais.
 */
export function StudioThumbnailsBanner() {
  const status = useStudioThumbnails()
  const run = useRunStudioThumbnails()
  const unqueue = useUnqueueStudioThumbnail()
  const { data: channels = [] } = useChannels()
  const [open, setOpen] = useState(false)
  const listId = useId()
  const data = status.data
  // Automação desligada pelo admin (Configurações → Prism Device): a faixa some.
  if (!data || isPaused(data)) return null

  // O Pages publica o front no merge, antes do pull do backend: o backend
  // antigo ainda não manda ``queue``.
  const queue = (data.queue as StudioThumbnailQueueItemOut[] | undefined) ?? []
  const feedback = run.isSuccess || run.isError || unqueue.isError
  if (queue.length === 0 && !data.running && !feedback) return null

  const busy = data.running || run.isPending
  const disabled = !canRun(data) || run.isPending
  const { failed, reason } = summarizeQueue(queue)
  const channelName = (slug: string) => channels.find((c) => c.slug === slug)?.name ?? slug
  const remove = (item: StudioThumbnailQueueItemOut) => {
    const name = item.title || item.youtube_video_id
    if (!window.confirm(`Tirar "${name}" da fila de capas? A drenagem não tenta mais a capa deste vídeo.`)) {
      return
    }
    unqueue.mutate(item.id)
  }
  return (
    <section className={cn(styles.banner, failed > 0 && styles.warning)} aria-label="Capas dos Shorts">
      <div className={styles.bar}>
        <span className={styles.icon} aria-hidden>
          {failed > 0 ? <AlertTriangle /> : <ImageUp />}
        </span>
        <p className={styles.headline}>
          <strong>{statusLine(data.enabled, data.running, data.pending)}</strong>
          {failed > 0 ? (
            <>
              <span className={styles.sep} aria-hidden>
                ·
              </span>
              <span className={styles.failed}>
                {failed} {failed === 1 ? 'falhou' : 'falharam'}
              </span>
              {reason ? (
                <span className={styles.reason} title={reason}>
                  — {reason}
                </span>
              ) : null}
            </>
          ) : null}
        </p>
        <div className={styles.actions}>
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<ImageUp />}
            disabled={disabled}
            title={data.is_admin ? undefined : 'Só o admin pode disparar a drenagem'}
            onClick={() => {
              run.mutate(undefined)
            }}
          >
            {busy ? 'Aplicando…' : 'Aplicar capas pendentes'}
          </Button>
          {queue.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={open}
              aria-controls={listId}
              trailingIcon={<ChevronDown className={cn(styles.chevron, open && styles.chevronOpen)} />}
              onClick={() => {
                setOpen((v) => !v)
              }}
            >
              {open ? 'Recolher' : 'Ver fila'}
            </Button>
          ) : null}
        </div>
      </div>

      {run.isSuccess && !run.data.started ? <p className={styles.note}>Nada na fila agora.</p> : null}
      {run.isSuccess && run.data.started ? (
        <p className={styles.note} role="status">
          {coversRunMessage(run.data)}
        </p>
      ) : null}
      {run.isError ? (
        <p className={styles.note} role="alert">
          {errorMessage(run.error, 'Não foi possível disparar agora.')}
        </p>
      ) : null}
      {data.enabled && !data.is_admin ? <p className={styles.note}>Só o admin pode disparar a drenagem.</p> : null}
      {unqueue.isError ? (
        <p className={styles.note} role="alert">
          {errorMessage(unqueue.error, 'Não foi possível tirar da fila.')}
        </p>
      ) : null}

      <ul id={listId} className={styles.queue} aria-label="Vídeos na fila de capas" hidden={!open}>
        {queue.map((item) => (
          <li key={item.id} className={styles.row}>
            <span className={styles.dot} style={{ background: channelColor(item.channel_slug) }} aria-hidden />
            <div className={styles.main}>
              <div className={styles.head}>
                <span className={styles.channel}>{channelName(item.channel_slug)}</span>
                <Chip variant={item.status === 'failed' ? 'warning' : 'default'}>
                  {attemptLabel(item, data.max_attempts)}
                </Chip>
              </div>
              <a
                className={styles.title}
                href={`https://www.youtube.com/shorts/${item.youtube_video_id}`}
                target="_blank"
                rel="noreferrer"
              >
                {item.title || item.youtube_video_id}
              </a>
              {item.error ? (
                <p className={styles.error} title={item.error}>
                  {item.error}
                </p>
              ) : null}
              <p className={styles.meta}>
                Na fila desde {formatRelativeDate(item.queued_at)}
                {item.last_attempt_at ? ` · última tentativa ${formatRelativeDate(item.last_attempt_at)}` : null}
              </p>
            </div>
            <IconButton
              variant="ghost"
              size="sm"
              aria-label={`Tirar "${item.title || item.youtube_video_id}" da fila`}
              title={data.running ? 'Espere a drenagem acabar para tirar da fila' : 'Tirar da fila'}
              disabled={data.running || unqueue.isPending}
              onClick={() => {
                remove(item)
              }}
            >
              <X />
            </IconButton>
          </li>
        ))}
      </ul>
    </section>
  )
}
